import { ADMIN_API_PREFIX, fillPath, route } from "./routes.js";
import { z } from "zod";
import { createOAuthClientDtoSchema, oauthClientSummarySchema, updateOAuthClientDtoSchema } from "@zion/sdk-contracts";
//#region src/oauth-clients.ts
/**
* `/v1/admin/oauth-clients` — the org's registered OAuth 2.1 clients.
*
* Producer `AdminOAuthClientsController` in core-api. Every shape here already lives in
* `@zion/sdk-contracts` (`oauth.ts`), so this file declares routes, one path builder and the
* request and response schemas over those shapes, and redeclares none of them.
*
* Permissions: the class carries `settings:identity:manage`; the list handler narrows to
* `settings:identity:read`. `NoDelegatedPrincipalGuard` sits on the class, so a token carrying
* `scp` is refused at every route here whatever permission it holds.
*
* Two facts the shell's `api/client.ts` gets wrong, and the controller is the fact:
* `deleteOAuthClient` is typed `Promise<void>` in the shell while the controller answers 200 with
* `{ message: string }`; and the `:id` param is a uuid (`oauthClientIdParamSchema`), which the
* shell interpolates without validating.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused and one new column cannot red every page that reads the route.
* Every request schema is `z.strictObject`, so a key the server would reject is refused before
* the request leaves the client.
* No shape in this file travels in both directions.
* The three row responses read {@link oauthClientSummaryViewSchema}, a
* `z.object(oauthClientSummarySchema.shape)` rebuild of the `@zion/sdk-contracts` shape (R-38).
* That package stays the shape's owner; a test pins the two key sets equal so the view cannot
* drift from it.
*/
/** The collection path. No parameter, so the OpenAPI form and the request form are the same. */
const ADMIN_OAUTH_CLIENTS_PATH = `${ADMIN_API_PREFIX}/oauth-clients`;
/** One client, in OpenAPI `{param}` form. Fill it with {@link adminOAuthClientPath}. */
const ADMIN_OAUTH_CLIENT_PATH_TEMPLATE = `${ADMIN_OAUTH_CLIENTS_PATH}/{id}`;
/**
* `GET /v1/admin/oauth-clients`. Every client row for the caller's org, with live grant counts
* and no secret — DP clients are public clients. Permission `settings:identity:read`.
*/
const LIST_OAUTH_CLIENTS_ROUTE = route("GET", ADMIN_OAUTH_CLIENTS_PATH);
/**
* `POST /v1/admin/oauth-clients`. Registers an app-bound public client; 201 with the created row,
* 400 on a bad redirect URI or scope, 409 when `clientId` is taken. Permission
* `settings:identity:manage`.
*/
const CREATE_OAUTH_CLIENT_ROUTE = route("POST", ADMIN_OAUTH_CLIENTS_PATH);
/**
* `PATCH /v1/admin/oauth-clients/{id}`. Edits one client, disable and enable included; 200 with
* the updated row, 404 for an unknown client. Permission `settings:identity:manage`.
*/
const UPDATE_OAUTH_CLIENT_ROUTE = route("PATCH", ADMIN_OAUTH_CLIENT_PATH_TEMPLATE);
/**
* `DELETE /v1/admin/oauth-clients/{id}`. Permanent, and it revokes every grant the client holds.
* Answers 200 with a message body, never 204. Permission `settings:identity:manage`.
*/
const DELETE_OAUTH_CLIENT_ROUTE = route("DELETE", ADMIN_OAUTH_CLIENT_PATH_TEMPLATE);
/**
* Fill {@link ADMIN_OAUTH_CLIENT_PATH_TEMPLATE} with one client id.
*
* @param id the `auth.oauth_clients` uuid, as `oauthClientSummarySchema.id` publishes it. Throws
*   when it is empty, longer than 128 characters, or carries a character outside the path-param
*   grammar.
* @returns the request path, each segment percent-encoded.
*/
function adminOAuthClientPath(id) {
	return fillPath(ADMIN_OAUTH_CLIENT_PATH_TEMPLATE, { id });
}
/**
* The client row every route here returns, relaxed to strip an unknown member (R-38).
*
* `oauthClientSummarySchema` in `@zion/sdk-contracts` (`oauth.ts`) owns this shape and stays its
* home. This is `z.object(upstream.shape)`: the same members, the same member schemas, the
* strictness dropped, because a response that refuses a member core-api adds reds every page that
* reads the route. The upstream carries no `.refine()`, so the rebuild loses nothing else, and
* `oauth-clients.test.ts` pins the two key sets equal so the view cannot drift from its owner.
*/
const oauthClientSummaryViewSchema = z.object(oauthClientSummarySchema.shape);
/** `GET /v1/admin/oauth-clients` response: every row, unpaged. */
const listOAuthClientsResponseSchema = z.array(oauthClientSummaryViewSchema);
/**
* `POST /v1/admin/oauth-clients` body. Owned by `@zion/sdk-contracts`
* (`createOAuthClientDtoSchema`): app-bound only, so `allowedScopes` is absent and sending it is
* a 400 under the strict pipe.
*/
const createOAuthClientRequestSchema = createOAuthClientDtoSchema;
/** `POST /v1/admin/oauth-clients` response: the created row. */
const createOAuthClientResponseSchema = oauthClientSummaryViewSchema;
/**
* `PATCH /v1/admin/oauth-clients/{id}` body. Owned by `@zion/sdk-contracts`
* (`updateOAuthClientDtoSchema`): `clientId` is immutable and absent, so an attempt to change it
* is a 400.
*/
const updateOAuthClientRequestSchema = updateOAuthClientDtoSchema;
/** `PATCH /v1/admin/oauth-clients/{id}` response: the updated row. */
const updateOAuthClientResponseSchema = oauthClientSummaryViewSchema;
/**
* `DELETE /v1/admin/oauth-clients/{id}` response.
*
* The route answers 200 with this body: `OAuthClientsService.remove` returns `{ message }` and
* the controller passes it through, so a client typing this route `void` discards the only thing
* it says.
*/
const deleteOAuthClientResponseSchema = z.object({ 
/** Human-readable confirmation from the service. Not a machine-readable code. */
message: z.string() });
/** Every route this file declares, for the package's route census. */
const oauthClientsRoutes = Object.freeze([
	LIST_OAUTH_CLIENTS_ROUTE,
	CREATE_OAUTH_CLIENT_ROUTE,
	UPDATE_OAUTH_CLIENT_ROUTE,
	DELETE_OAUTH_CLIENT_ROUTE
]);
//#endregion
export { ADMIN_OAUTH_CLIENTS_PATH, ADMIN_OAUTH_CLIENT_PATH_TEMPLATE, CREATE_OAUTH_CLIENT_ROUTE, DELETE_OAUTH_CLIENT_ROUTE, LIST_OAUTH_CLIENTS_ROUTE, UPDATE_OAUTH_CLIENT_ROUTE, adminOAuthClientPath, createOAuthClientRequestSchema, createOAuthClientResponseSchema, deleteOAuthClientResponseSchema, listOAuthClientsResponseSchema, oauthClientSummaryViewSchema, oauthClientsRoutes, updateOAuthClientRequestSchema, updateOAuthClientResponseSchema };

//# sourceMappingURL=oauth-clients.js.map